import React, { useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { useViewer } from "core/components/ViewerProvider";
import { useArena } from "core/components/ArenaProvider";
import BanScreen from "core/components/BanScreen";
import Page from "arena/components/Page";
import Unsubscribe from "arena/components/Unsubscribe";
import DisableSubscription from "arena/components/DisableSubscription";
import InvalidToken from "arena/components/InvalidToken";
import AppShell from "arena/components/AppShell";
import Search from "arena/components/Search";
import Contents from "arena/components/Contents";
import Favorites from "arena/components/Favorites";
import Video from "arena/components/Video";
import Shorty from "arena/components/Shorty";
import Post from "arena/components/Post";
import DigitalAsset from "arena/components/DigitalAsset";
import Book from "arena/components/Book";
import BookReader from "arena/components/BookReader";
import Tag from "arena/components/Tag";
import Purchases from "arena/components/ProductPurchases";
import Stream from "arena/components/Stream";
import Channel from "arena/components/Channel";
import ChannelDialogContent from "arena/components/ChannelDialogContent";
import useProxyChannel from "arena/hooks/useProxyChannel";
import Collection from "arena/components/Collection";
import Product from "arena/components/Product";
import ProductPaymentCard from "arena/components/ProductPaymentCard";
import InvalidOauth from "arena/components/InvalidOauth";
import SuccessAuth from "arena/components/SuccessAuth";
import AuthRequired from "arena/components/AuthRequired";
import useOauthResult from "arena/hooks/useOauthResult";
import trackEvent from "arena/utils/trackEvent";
import Document from "arena/components/Document";
import AuthByTelegram from "arena/components/AuthByTelegram";
import RouteDialog from "arena/components/RouteDialog";
import RouteDialogOpenContext from "arena/components/RouteDialog/RouteDialogOpenContext";
import History from "arena/components/History";
import Reads from "arena/components/Reads";
import {
  useReactRouterIntegration,
  isTelegramMiniApp,
} from "core/utils/telegram";

import { useLocation, useNavigate } from "react-router-dom";
import LoadingScreen from "core/components/LoadingScreen";

export default function AppRoutes() {
  const location = useLocation();
  const navigate = useNavigate();

  useReactRouterIntegration(location, navigate);

  return (
    <Routes>
      <Route path="/product/:productId/pay" element={<ProductPaymentCard />} />
      <Route path="/book/:contentId/read" element={<BookReader />} />
      <Route path="*" element={<ArenaAppRoutes />} />
    </Routes>
  );
}

function ArenaAppRoutes() {
  const viewer = useViewer();
  const arena = useArena();
  const successAuth = useOauthResult();
  const location = useLocation();
  // Прокси-страница канала: диалог /tv/:slug открывается на её адресе
  const proxyChannel = useProxyChannel(location);

  useEffect(() => {
    trackEvent("pageView", { arena });
  }, []);

  if (isTelegramMiniApp) {
    if (typeof viewer === "undefined") {
      return <LoadingScreen />;
    }

    if (viewer === null) {
      return <AuthByTelegram />;
    }
  }

  if (viewer?.isBanned) {
    return <BanScreen />;
  }

  if (successAuth) {
    return <SuccessAuth />;
  }

  const background = location?.state?.background;

  return (
    <AppShell>
      <RouteDialogOpenContext.Provider value={Boolean(background)}>
        <Routes location={background || location}>
          <Route path="/tag/:tagId" element={<Tag />} />
          <Route path="/collection/:contentId" element={<Collection />} />

          <Route path="/favorites" element={<Favorites />} />
          <Route path="/contents" element={<Contents />} />
          <Route path="/search" element={<Search />} />
          <Route path="/history" element={<History />} />
          <Route path="/reads" element={<Reads />} />
          <Route path="/invalidOauth" element={<InvalidOauth />} />
          <Route path="/unsubscribe/:token" element={<Unsubscribe />} />

          <Route
            path="/disableSubscription/:token"
            element={<DisableSubscription />}
          />
          <Route path="/invalidToken" element={<InvalidToken />} />

          <Route
            path="/purchases"
            element={viewer ? <Purchases /> : <AuthRequired />}
          />

          <Route path="/document/:slug" element={<Document />} />

          <Route path="/video/:contentId" element={null} />
          <Route path="/shorty/:contentId" element={null} />
          <Route path="/stream/:contentId" element={null} />
          <Route path="/tv/:slug" element={null} />
          <Route path="/channel/:contentId" element={null} />
          <Route path="/post/:postId" element={null} />
          <Route path="/digitalAsset/:digitalAssetId" element={null} />
          <Route path="/book/:contentId" element={null} />
          <Route path="/product/:productId" element={null} />

          <Route path="*" element={<Page />} />
        </Routes>
      </RouteDialogOpenContext.Provider>

      <RouteDialog path="/video/:contentId" element={<Video />} />
      <RouteDialog path="/shorty/:contentId" element={<Shorty />} fullscreen />
      <RouteDialog path="/stream/:contentId" element={<Stream />} />
      <RouteDialog
        path="/tv/:slug"
        element={<ChannelDialogContent proxyChannel={proxyChannel} />}
        virtualMatch={proxyChannel?.match}
      />
      <RouteDialog path="/channel/:contentId" element={<Channel />} />
      <RouteDialog path="/post/:postId" element={<Post />} />
      <RouteDialog
        path="/digitalAsset/:digitalAssetId"
        element={<DigitalAsset />}
      />
      <RouteDialog path="/book/:contentId" element={<Book />} />
      <RouteDialog path="/product/:productId" element={<Product />} />
    </AppShell>
  );
}
